// =====================================================
// PracticeWord - Image orientation utilities
//
// Mobile phone photos usually carry an EXIF Orientation tag (1-8) that
// describes how the camera-held image should be rotated for display.
// Browsers normally apply this tag automatically when rendering via <img>,
// but the pixel bytes we send to the Worker via base64 are the raw
// (un-rotated) image. Workers AI's vision model then sees a sideways image
// and OCR produces nonsense.
//
// This module:
//   1. Reads the EXIF orientation value (1-8) from a JPEG.
//   2. Draws the image onto a Canvas with the correct rotation/flip applied
//      so the bytes we send to the Worker are upright.
//   3. Falls back to the original orientation if EXIF parsing fails (PNG,
//      HEIC, no EXIF, etc.) — better to send something than nothing.
//
// Mapping (reference: W3C / Exif 2.2 §4.6.4):
//   1 → no transform
//   2 → mirror horizontal
//   3 → rotate 180°
//   4 → mirror vertical
//   5 → rotate 90° CW + mirror horizontal
//   6 → rotate 90° CW
//   7 → rotate 270° CW + mirror horizontal
//   8 → rotate 270° CW (== 90° CCW)
// =====================================================

export interface AutoOrientedImage {
  /** The image bytes after orientation has been baked in (canonical upright). */
  base64: string;
  /** `data:<mime>;base64,<base64>` ready for any consumer. */
  dataUri: string;
  /** Detected orientation value (1-8), or null if none. */
  orientation: number | null;
}

/**
 * Read the EXIF orientation value (1-8) from a JPEG. Returns null for non-JPEG
 * files or files without an APP1/Exif segment. Fast: stops scanning after the
 * first 256 KiB.
 */
export async function readJpegOrientation(file: File): Promise<number | null> {
  if (!/jpe?g/i.test(file.type) && !/\.jpe?g$/i.test(file.name)) return null;
  const HEAD = Math.min(file.size, 256 * 1024);
  const buf = new Uint8Array(await file.slice(0, HEAD).arrayBuffer());

  let i = 0;
  while (i + 4 < buf.length) {
    if (buf[i] !== 0xff) return null;
    const marker = buf[i + 1];
    if (marker === 0xd8) {
      i += 2;
      continue;
    }
    if ((marker >= 0xd0 && marker <= 0xd9) || marker === 0x01) {
      i += 2;
      continue;
    }
    const len = (buf[i + 2] << 8) | buf[i + 3];
    if (marker === 0xe1) {
      // APP1 segment: look for "Exif\0\0" + TIFF header.
      if (
        buf[i + 4] === 0x45 && buf[i + 5] === 0x78 && buf[i + 6] === 0x69 && buf[i + 7] === 0x66 &&
        buf[i + 8] === 0x00 && buf[i + 9] === 0x00
      ) {
        const tiffStart = i + 10;
        const littleEndian = buf[tiffStart] === 0x49 && buf[tiffStart + 1] === 0x49;
        const readU16 = (off: number) =>
          littleEndian
            ? buf[off] | (buf[off + 1] << 8)
            : (buf[off] << 8) | buf[off + 1];
        const readU32 = (off: number) =>
          littleEndian
            ? (buf[off] | (buf[off + 1] << 8) | (buf[off + 2] << 16) | (buf[off + 3] << 24)) >>> 0
            : ((buf[off] << 24) | (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3]) >>> 0;
        const ifd0Offset = readU32(tiffStart + 4);
        const ifd0Start = tiffStart + ifd0Offset;
        const entries = readU16(ifd0Start);
        for (let e = 0; e < entries; e++) {
          const entryOff = ifd0Start + 2 + e * 12;
          const tag = readU16(entryOff);
          if (tag === 0x0112) {
            const value = readU16(entryOff + 8);
            if (value >= 1 && value <= 8) return value;
            return null;
          }
        }
        return null;
      }
    }
    if (len <= 0) return null;
    i += 2 + len;
  }
  return null;
}

interface OrientTransform {
  /** Rotation in degrees clockwise. */
  rotate: 0 | 90 | 180 | 270;
  /** Whether to mirror the image horizontally AFTER rotation. */
  mirrorX: boolean;
}

/** Map EXIF orientation → required canvas transform. */
const ORIENT_TABLE: Record<number, OrientTransform> = {
  1: { rotate: 0, mirrorX: false },
  2: { rotate: 0, mirrorX: true },
  3: { rotate: 180, mirrorX: false },
  4: { rotate: 180, mirrorX: true },
  5: { rotate: 90, mirrorX: true },
  6: { rotate: 90, mirrorX: false },
  7: { rotate: 270, mirrorX: true },
  8: { rotate: 270, mirrorX: false },
};

/**
 * Read the file, detect orientation, draw to a Canvas with the correct
 * rotation/flip, and return upright JPEG bytes as base64.
 */
export async function autoOrient(file: File): Promise<AutoOrientedImage> {
  const orientation = await readJpegOrientation(file);

  // Load via <img> so the browser applies whatever default rendering it can.
  const url = URL.createObjectURL(file);
  let img: HTMLImageElement;
  try {
    img = await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }

  const w0 = img.naturalWidth;
  const h0 = img.naturalHeight;
  const transform = ORIENT_TABLE[orientation ?? 1];
  const swap = transform.rotate === 90 || transform.rotate === 270;
  const w = swap ? h0 : w0;
  const h = swap ? w0 : h0;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("无法获取 Canvas 上下文");

  if (transform.rotate !== 0 || transform.mirrorX) {
    ctx.translate(w / 2, h / 2);
    if (transform.rotate !== 0) {
      ctx.rotate((transform.rotate * Math.PI) / 180);
    }
    if (transform.mirrorX) {
      ctx.scale(-1, 1);
    }
    ctx.translate(-w0 / 2, -h0 / 2);
  }
  ctx.drawImage(img, 0, 0, w0, h0);

  const dataUri = canvas.toDataURL("image/jpeg", 0.92);
  const base64 = dataUri.slice(dataUri.indexOf(",") + 1);
  return { base64, dataUri, orientation };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("图片解码失败"));
    img.src = url;
  });
}